import logo from '@/assets/hiredcoach-logo.svg';
import { demoEducation, demoEmployment, demoJob } from '@/components/demo/landingDemoData';
import { DashboardLayout } from "@/components/layouts/DashboardLayout";
import PremiumLockModal from '@/components/subscription/PremiumLockModal';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { EducationResponse, EmploymentResponse, JobApiResponse, PitchAnalysisCreate, useApiClient } from '@/lib/api';
import { sortJobsByPriorityAndRecency } from '@/lib/job-status';
import { ArrowLeft, Hourglass, Lock, Mic, StopCircle } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

interface PitchNewProps {
    demoMode?: boolean;
    embed?: boolean;
}

const PitchNew: React.FC<PitchNewProps> = ({ demoMode = false, embed = false }) => {
    const location = useLocation();
    // Helper to get query params
    function getQueryParam(param: string): string | null {
        const params = new URLSearchParams(location.search);
        return params.get(param);
    }

    const navigate = useNavigate();
    const { api } = useApiClient();
    const { user } = useAuth();
    const isPremium = (user?.subscription_mode ?? 'free') === 'premium';
    
    // Form state
    const [pitchText, setPitchText] = useState('');
    const [selectedTimeLimit, setSelectedTimeLimit] = useState<30 | 60 | 90>(30);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Focus selection state
    const [selectedFocus, setSelectedFocus] = useState<'general' | 'employment' | 'education'>('general');
    const [selectedEmploymentId, setSelectedEmploymentId] = useState<string>('');
    const [selectedEducationId, setSelectedEducationId] = useState<string>('');
    
    // Job targeting state (separate from focus)
    const [jobTargeting, setJobTargeting] = useState<'general' | 'specific'>('general');
    const [selectedJobId, setSelectedJobId] = useState<string>('');
    
    // Data for dropdowns
    const [jobs, setJobs] = useState<JobApiResponse[]>([]);
    // For jobId from query
    const [jobIdFromQuery, setJobIdFromQuery] = useState<string | null>(null);

    const [employments, setEmployments] = useState<EmploymentResponse[]>([]);
    const [educations, setEducations] = useState<EducationResponse[]>([]);
    const [loadingData, setLoadingData] = useState(false);
    
    // Recording state
    const [isRecording, setIsRecording] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);
    // Premium lock modal state
    const [showPremiumModal, setShowPremiumModal] = useState(false);
    const [premiumFeatureLabel, setPremiumFeatureLabel] = useState<string | undefined>(undefined);
    
    // Grading and polling state
    const [isGrading, setIsGrading] = useState(false);
    const [submittedPitchId, setSubmittedPitchId] = useState<string | null>(null);
    const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);
    const [gradingJobName, setGradingJobName] = useState<string | null>(null);
    const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
    const [audioChunks, setAudioChunks] = useState<Blob[]>([]);
    const recordingStartTimestampRef = useRef<number | null>(null); // ms since epoch
    const [recordingTime, setRecordingTime] = useState(0); // still used for live display, but not for lastRecordingTime
    const [timeRemaining, setTimeRemaining] = useState(selectedTimeLimit);
    const [lastRecordingTime, setLastRecordingTime] = useState<number | null>(null);
    
    const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
    const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Cleanup timers on unmount
    useEffect(() => {
        return () => {
            if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current);
            }
            if (countdownTimerRef.current) {
                clearInterval(countdownTimerRef.current);
            }
        };
    }, []);

    // Update time remaining when time limit changes
    useEffect(() => {
        if (!isRecording) {
            setTimeRemaining(selectedTimeLimit);
        }
    }, [selectedTimeLimit, isRecording]);

    // On mount: check for jobId query param and set targeting if present
    useEffect(() => {
        if (demoMode) return;
        const jobId = getQueryParam('jobId');
        if (jobId) {
            setJobIdFromQuery(jobId);
            if (isPremium) {
                setJobTargeting('specific');
                setSelectedJobId(jobId);
            } else {
                // Non-premium users cannot target a specific job
                setPremiumFeatureLabel('Target Job');
                setShowPremiumModal(true);
            }
        }
    }, [location.search, isPremium]);

    // Fetch data for dropdowns
    useEffect(() => {
        const fetchData = async () => {
            setLoadingData(true);
            try {
                if (demoMode) {
                    // Use demo data
                    setJobs([demoJob]);
                    setEmployments(demoEmployment);
                    setEducations(demoEducation);
                } else {
                    const [jobsData, employmentsData, educationsData] = await Promise.all([
                        api.getJobs(),
                        api.getEmployments(),
                        api.getEducations()
                    ]);
                    
                    // Keep active jobs ahead of terminal outcomes, then recency.
                    setJobs(sortJobsByPriorityAndRecency(jobsData));
                    setEmployments(employmentsData.sort((a, b) => new Date(b.start_date).getTime() - new Date(a.start_date).getTime()));
                    setEducations(educationsData.sort((a, b) => new Date(b.start_date).getTime() - new Date(a.start_date).getTime()));
                }
            } catch (error) {
                console.error('Failed to fetch data for dropdowns:', error);
                if (!demoMode) {
                    toast.error('Failed to load data for focus selection');
                }
            } finally {
                setLoadingData(false);
            }
        };
        
        fetchData();
    }, [demoMode, api]);

    // Cleanup polling interval on unmount
    useEffect(() => {
        return () => {
            if (pollingInterval) {
                clearInterval(pollingInterval);
            }
        };
    }, [pollingInterval]);

    // Polling function to check pitch completion
    const startPolling = (pitchId: string) => {
        if (demoMode) return;
        const interval = setInterval(async () => {
            try {
                const updatedPitch = await api.getPitchAnalysis(pitchId);
                if (updatedPitch.status !== 'grading') {
                    // Grading complete, navigate to results
                    setIsGrading(false);
                    clearInterval(interval);
                    setPollingInterval(null);
                    toast.success('Pitch analysis complete!');
                    if (jobIdFromQuery) {
    navigate(`/pitch/${pitchId}?jobId=${jobIdFromQuery}`);
} else {
    navigate(`/pitch/${pitchId}`);
}
                }
            } catch (error) {
                console.error('Failed to poll pitch status:', error);
                setIsGrading(false);
                clearInterval(interval);
                setPollingInterval(null);
                toast.error('Failed to check pitch status');
            }
        }, 3000); // Poll every 3 seconds
        
        setPollingInterval(interval);
    };

    // Handle focus change
    const handleFocusChange = (focus: 'general' | 'employment' | 'education') => {
        // Prevent changing to employment if no employment data
        if (focus === 'employment' && !loadingData && employments.length === 0) {
            toast.error('No employment experience found. Please add employment history first.');
            return;
        }
        // Prevent changing to education if no education data
        if (focus === 'education' && !loadingData && educations.length === 0) {
            toast.error('No education experience found. Please add education history first.');
            return;
        }
        
        setSelectedFocus(focus);
        // Reset selections when focus changes
        setSelectedEmploymentId('');
        setSelectedEducationId('');
    };

    // Handle job targeting change
    const handleJobTargetingChange = (targeting: 'general' | 'specific') => {
        // Prevent changing to specific if no jobs available
        if (targeting === 'specific' && !loadingData && jobs.length === 0) {
            toast.error('No jobs found. Please add a job first.');
            return;
        }
        // Premium lock for specific targeting
        if (targeting === 'specific' && !demoMode && !isPremium) {
            setPremiumFeatureLabel('Target Job');
            setShowPremiumModal(true);
            return;
        }
        
        setJobTargeting(targeting);
        // Reset job selection when targeting changes
        setSelectedJobId('');
    };

    const isMicrophoneSupported = useCallback(() => {
        return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia && window.MediaRecorder);
    }, []);

    const getSupportedAudioFormat = useCallback(() => {
        const formats = [
            'audio/webm;codecs=opus',
            'audio/webm',
            'audio/mp4',
            'audio/ogg;codecs=opus',
            'audio/wav'
        ];
        
        for (const format of formats) {
            if (MediaRecorder.isTypeSupported(format)) {
                return format;
            }
        }
        return 'audio/webm'; // fallback
    }, []);

    const handleMicClick = async () => {
        if (demoMode) return;
        if (!isMicrophoneSupported()) {
            toast.error('Microphone not supported on this device');
            return;
        }

        if (isRecording) {
            // Stop recording
            if (mediaRecorder && mediaRecorder.state === 'recording') {
                mediaRecorder.stop();
            }
            return;
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                    sampleRate: 44100
                }
            });

            const format = getSupportedAudioFormat();
            const recorder = new MediaRecorder(stream, { mimeType: format });
            const chunks: Blob[] = [];

            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    chunks.push(e.data);
                }
            };

            recorder.onstop = async () => {
                // Stop all tracks
                stream.getTracks().forEach(track => track.stop());
                
                // Clear timers
                if (recordingTimerRef.current) {
                    clearInterval(recordingTimerRef.current);
                    recordingTimerRef.current = null;
                }
                if (countdownTimerRef.current) {
                    clearInterval(countdownTimerRef.current);
                    countdownTimerRef.current = null;
                }

                setIsRecording(false);
                let duration = 0;
                if (recordingStartTimestampRef.current) {
                    duration = Math.round((Date.now() - recordingStartTimestampRef.current) / 1000);
                    recordingStartTimestampRef.current = null;
                }
                setLastRecordingTime(duration > 0 ? duration : 0);
                setRecordingTime(0);
                setTimeRemaining(selectedTimeLimit);

                if (chunks.length === 0) {
                    toast.error('No audio data recorded');
                    return;
                }

                try {
                    setIsTranscribing(true);
                    const audioBlob = new Blob(chunks, { type: format });
                    
                    if (audioBlob.size === 0) {
                        toast.error('Empty audio recording');
                        return;
                    }

                    const formData = new FormData();
                    const extension = format.includes('webm') ? '.webm' : 
                                   format.includes('mp4') ? '.mp4' : 
                                   format.includes('ogg') ? '.ogg' : '.wav';
                    formData.append('file', audioBlob, `recording${extension}`);

                    const transcription = await api.transcribePitchAudio(formData);
                    
                    if (transcription && transcription.trim()) {
                        let cleaned = transcription.trim();
                        if (cleaned.startsWith('"') && cleaned.endsWith('"')) {
                            cleaned = cleaned.slice(1, -1).trim();
                        }
                        setPitchText(cleaned);
                    } else {
                        toast.error('No speech detected in recording');
                    }
                } catch (error) {
                    console.error('Transcription error:', error);
                    toast.error('Failed to transcribe audio. Please try again.');
                } finally {
                    setIsTranscribing(false);
                }
            };

            recorder.onerror = (event: any) => {
                console.error('MediaRecorder error:', event);
                toast.error('Recording error occurred');
                setIsRecording(false);
                let duration = 0;
                if (recordingStartTimestampRef.current) {
                    duration = Math.round((Date.now() - recordingStartTimestampRef.current) / 1000);
                    recordingStartTimestampRef.current = null;
                }
                setLastRecordingTime(duration > 0 ? duration : 0);
                setRecordingTime(0);
                setTimeRemaining(selectedTimeLimit);
                
                if (recordingTimerRef.current) {
                    clearInterval(recordingTimerRef.current);
                    recordingTimerRef.current = null;
                }
                if (countdownTimerRef.current) {
                    clearInterval(countdownTimerRef.current);
                    countdownTimerRef.current = null;
                }
            };

            setMediaRecorder(recorder);
            setAudioChunks(chunks);
            setIsRecording(true);
            setRecordingTime(0);
            setTimeRemaining(selectedTimeLimit);

            // --- Store start timestamp ---
            recordingStartTimestampRef.current = Date.now();

            // Start recording
            recorder.start(1000); // Collect data every second for better mobile compatibility

            // Start recording timer
            recordingTimerRef.current = setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);

            // Start countdown timer
            countdownTimerRef.current = setInterval(() => {
                setTimeRemaining(prev => {
                    if (prev <= 1) {
                        // Auto-stop recording when time limit reached
                        if (recorder && recorder.state === 'recording') {
                            recorder.stop();
                        }
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

        } catch (error: any) {
            console.error('Microphone access error:', error);
            
            let errorMessage = 'Failed to access microphone';
            if (error.name === 'NotAllowedError') {
                errorMessage = 'Microphone access denied. Please allow microphone access and try again.';
            } else if (error.name === 'NotFoundError') {
                errorMessage = 'No microphone found. Please connect a microphone and try again.';
            } else if (error.name === 'NotSupportedError') {
                errorMessage = 'Microphone not supported on this device.';
            } else if (error.name === 'NotReadableError') {
                errorMessage = 'Microphone is being used by another application.';
            }
            
            toast.error(errorMessage);
        }
    };

    const getTimerColor = (timeRemaining: number) => {
        if (timeRemaining <= 0) {
            return 'text-red-600';
        } else if (timeRemaining <= 10) {
            return 'text-yellow-500';
        } else {
            return 'text-green-600';
        }
    };

    const getTimeLimitColor = (timeLimit: number) => {
        switch (timeLimit) {
            case 30: return 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200';
            case 60: return 'bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200';
            case 90: return 'bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-200';
            default: return 'bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200';
        }
    };

    const handleSubmit = async () => {
        if (demoMode) return;
        if (!pitchText.trim()) {
            toast.error('Please enter your pitch text');
            return;
        }

        // Validate focus selection
        if (selectedFocus === 'employment' && !selectedEmploymentId) {
            toast.error('Please select an employment experience for your pitch focus');
            return;
        }
        if (selectedFocus === 'education' && !selectedEducationId) {
            toast.error('Please select an education experience for your pitch focus');
            return;
        }
        
        // Validate job targeting
        if (jobTargeting === 'specific' && !selectedJobId) {
            toast.error('Please select a target job');
            return;
        }

        setIsSubmitting(true);
        try {
            const pitchData: PitchAnalysisCreate = {
                content: pitchText,
                time_limit: selectedTimeLimit,
                pitch_type: selectedFocus,
            };

            // Only include optional fields if they have values
            if (jobTargeting === 'specific' && selectedJobId) {
                pitchData.job_id = selectedJobId;
            }
            if (selectedFocus === 'employment' && selectedEmploymentId) {
                pitchData.employment_id = selectedEmploymentId;
            }
            if (selectedFocus === 'education' && selectedEducationId) {
                pitchData.education_id = selectedEducationId;
            }

            const newPitch = await api.createPitchAnalysis(pitchData);
            toast.success('Pitch submitted for analysis!');
            
            // Start polling for completion instead of navigating immediately
            setSubmittedPitchId(newPitch.id);
            setIsGrading(true);
            
            // Capture job name if targeting a specific job
            if (jobTargeting === 'specific' && selectedJobId) {
                const selectedJob = jobs.find(job => job.id === selectedJobId);
                if (selectedJob) {
                    const jobName = selectedJob.title ? `${selectedJob.title} - ${selectedJob.company}` : selectedJob.company;
                    setGradingJobName(jobName);
                }
            }
            
            startPolling(newPitch.id);
        } catch (error: any) {
            console.error('Failed to submit pitch:', error);
            if (error?.statusCode === 405 && !demoMode) {
                // Subscription-locked action
                setShowPremiumModal(true);
            } else {
                toast.error('Failed to submit pitch for analysis - try again later');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const renderMain = () => (
            <div className={`container mx-auto ${embed ? 'px-0 py-4' : 'px-4 sm:px-6 lg:px-8 py-6 sm:py-8'} max-w-4xl`}>
                {/* Header */}
                <div className="flex items-center gap-4 mb-8">
                    <Button 
                        variant="ghost" 
                        onClick={() => {
                            if (demoMode) return;
                            if (jobIdFromQuery) {
                                navigate(`/jobs/${jobIdFromQuery}`);
                            } else {
                                navigate('/pitch');
                            }
                        }}
                        className="flex items-center gap-2"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back
                    </Button>
                </div>

                {/* Main Content */}
                <div className="space-y-8">
                    <Card>
                        <CardHeader className="text-center">
                            <div className="flex justify-center mb-4">
                                <img src={logo} alt="HiredCoach" className="h-36 w-auto" />
                            </div>
                            <CardTitle className="text-2xl sm:text-3xl">Practice The <b>"YOU"</b> Elevator Pitch</CardTitle>
                            <CardDescription className="text-base">
                                Mastering the first impression story about yourself is crucial in interviews and networking events. Practice regularly to learn how to tell your story.
                            </CardDescription>
                        </CardHeader>
                    </Card>

                    {/* Job Targeting Section */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-xl">Job Targeting</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-3">
                                <Label>Who is this pitch for?</Label>
                                <div className="grid grid-cols-2 gap-3">
                                    {/* General targeting pill */}
                                    <button
                                        type="button"
                                        onClick={() => handleJobTargetingChange('general')}
                                        className={`group relative rounded-xl p-3 sm:p-4 shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-300 ${
                                            jobTargeting === 'general'
                                                ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white'
                                                : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-200'
                                        }`}
                                    >
                                        <div className="flex flex-col items-center gap-2 text-center">
                                            <div className="text-sm sm:text-base font-semibold">General</div>
                                            <div className="text-xs opacity-80">Any audience</div>
                                        </div>
                                    </button>

                                    {/* Specific job targeting pill */}
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (!loadingData && jobs.length === 0) return;
                                            if (!demoMode && !isPremium) {
                                                setPremiumFeatureLabel('Target Job');
                                                setShowPremiumModal(true);
                                                return;
                                            }
                                            handleJobTargetingChange('specific');
                                        }}
                                        className={`group relative rounded-xl p-3 sm:p-4 shadow-md transition-all duration-300 ${
                                            !loadingData && jobs.length === 0
                                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                                                : !demoMode && !isPremium
                                                ? 'bg-white text-gray-500 border border-gray-200 opacity-70'
                                                : jobTargeting === 'specific'
                                                ? 'bg-gradient-to-br from-indigo-500 to-indigo-600 text-white hover:shadow-lg transform hover:scale-105'
                                                : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 hover:shadow-lg transform hover:scale-105'
                                        }`}
                                    >
                                        <div className="flex flex-col items-center gap-2 text-center">
                                            <div className="text-sm sm:text-base font-semibold">Target Job</div>
                                            <div className="text-xs opacity-80">
                                                {!loadingData && jobs.length === 0 ? 'No jobs available' : 'Specific role'}
                                            </div>
                                        </div>
                                        {/* Premium lock badge */}
                                        {!demoMode && !isPremium && (
                                          <div className="absolute -top-3 -right-3 z-10">
                                            <div className="rounded-full bg-white shadow-md p-1.5 ring-1 ring-slate-200">
                                              <Lock className="h-6 w-6 text-slate-700" />
                                            </div>
                                          </div>
                                        )}
                                    </button>
                                </div>
                            </div>

                            {/* Specific Job Selection */}
                            {/* Show job summary if jobId is preselected, else show job selector */}
                            {jobTargeting === 'specific' && jobIdFromQuery && selectedJobId ? (
                                <div className="space-y-2">
                                    <Label>Targeting Job:</Label>
                                    <div className="flex items-center gap-2 bg-blue-50 rounded p-3 border border-blue-200">
                                        <span className="font-semibold text-blue-900">
                                            {(() => {
                                                const job = jobs.find(j => j.id === selectedJobId);
                                                if (!job) return selectedJobId;
                                                return job.title ? `${job.title} - ${job.company}` : job.company;
                                            })()}
                                        </span>
                                    </div>
                                </div>
                            ) : jobTargeting === 'specific' && (
                                <div className="space-y-2">
                                    <Label htmlFor="job-select">What job do we want to focus on for this pitch?</Label>
                                    <Select value={selectedJobId} onValueChange={setSelectedJobId}>
                                        <SelectTrigger id="job-select">
                                            <SelectValue placeholder="Select a job to target" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {loadingData && !demoMode ? (
                                                <SelectItem value="loading" disabled>Loading jobs...</SelectItem>
                                            ) : jobs.length === 0 ? (
                                                <SelectItem value="no-jobs" disabled>No jobs found</SelectItem>
                                            ) : (
                                                jobs.map((job) => (
                                                    <SelectItem key={job.id} value={job.id}>
                                                        {job.title ? job.title + ' - ' + job.company : job.company}
                                                    </SelectItem>
                                                ))
                                            )}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Focus Selection */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-xl">Pitch Focus</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-3">
                                <Label>What should your pitch highlight?</Label>
                                <div className="grid grid-cols-3 gap-3">
                                    {/* You pill */}
                                    <button
                                        type="button"
                                        onClick={() => handleFocusChange('general')}
                                        className={`group relative rounded-xl p-3 sm:p-4 shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-300 ${
                                            selectedFocus === 'general'
                                                ? 'bg-gradient-to-br from-purple-500 to-purple-600 text-white'
                                                : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-200'
                                        }`}
                                    >
                                        <div className="flex flex-col items-center gap-2 text-center">
                                            <div className="text-sm sm:text-base font-semibold">You</div>
                                            <div className="text-xs opacity-80">General story</div>
                                        </div>
                                    </button>

                                    {/* Employment pill */}
                                    <button
                                        type="button"
                                        onClick={() => handleFocusChange('employment')}
                                        disabled={!loadingData && employments.length === 0}
                                        className={`group relative rounded-xl p-3 sm:p-4 shadow-md transition-all duration-300 ${
                                            !loadingData && employments.length === 0
                                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                                                : selectedFocus === 'employment'
                                                ? 'bg-gradient-to-br from-green-500 to-green-600 text-white hover:shadow-lg transform hover:scale-105'
                                                : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 hover:shadow-lg transform hover:scale-105'
                                        }`}
                                    >
                                        <div className="flex flex-col items-center gap-2 text-center">
                                            <div className="text-sm sm:text-base font-semibold">Employment</div>
                                            <div className="text-xs opacity-80">
                                                {!loadingData && employments.length === 0 ? 'No employment' : 'Past work'}
                                            </div>
                                        </div>
                                    </button>

                                    {/* Education pill */}
                                    <button
                                        type="button"
                                        onClick={() => handleFocusChange('education')}
                                        disabled={!loadingData && educations.length === 0}
                                        className={`group relative rounded-xl p-3 sm:p-4 shadow-md transition-all duration-300 ${
                                            !loadingData && educations.length === 0
                                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                                                : selectedFocus === 'education'
                                                ? 'bg-gradient-to-br from-orange-500 to-orange-600 text-white hover:shadow-lg transform hover:scale-105'
                                                : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 hover:shadow-lg transform hover:scale-105'
                                        }`}
                                    >
                                        <div className="flex flex-col items-center gap-2 text-center">
                                            <div className="text-sm sm:text-base font-semibold">Education</div>
                                            <div className="text-xs opacity-80">
                                                {!loadingData && educations.length === 0 ? 'No education' : 'School focus'}
                                            </div>
                                        </div>
                                    </button>
                                </div>
                            </div>

                            {/* Employment Selection */}
                            {selectedFocus === 'employment' && (
                                <div className="space-y-2">
                                    <Label htmlFor="employment-select">What employment experience from your resume do you want to talk about?</Label>
                                    <Select value={selectedEmploymentId} onValueChange={setSelectedEmploymentId}>
                                        <SelectTrigger id="employment-select">
                                            <SelectValue placeholder="Select employment to highlight" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {loadingData ? (
                                                <SelectItem value="loading" disabled>Loading employment...</SelectItem>
                                            ) : employments.length === 0 ? (
                                                <SelectItem value="no-employment" disabled>No employment found</SelectItem>
                                            ) : (
                                                employments.map((employment) => (
                                                    <SelectItem key={employment.id} value={employment.id}>
                                                        {employment.title ? employment.title + ' - ' + employment.company : employment.company}
                                                    </SelectItem>
                                                ))
                                            )}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}

                            {/* Education Selection */}
                            {selectedFocus === 'education' && (
                                <div className="space-y-2">
                                    <Label htmlFor="education-select">What education experience from your resume do you want to talk about?</Label>
                                    <Select value={selectedEducationId} onValueChange={setSelectedEducationId}>
                                        <SelectTrigger id="education-select">
                                            <SelectValue placeholder="Select education to highlight" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {loadingData ? (
                                                <SelectItem value="loading" disabled>Loading education...</SelectItem>
                                            ) : educations.length === 0 ? (
                                                <SelectItem value="no-education" disabled>No education found</SelectItem>
                                            ) : (
                                                educations.map((education) => (
                                                    <SelectItem key={education.id} value={education.id}>
                                                        {[education.degree_type, education.major, education.institution]
                                                            .filter(Boolean)
                                                            .join(' - ')}
                                                    </SelectItem>
                                                ))
                                            )}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Time Limit Selection */}
                    <Card>
                        <CardContent className="p-6 sm:p-8">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full">
                                {[30, 60, 90].map((timeLimit) => {
                                    const locked = !demoMode && !isPremium && timeLimit > 30;
                                    return (
                                        <button
                                            key={timeLimit}
                                            onClick={() => {
                                                if (locked) {
                                                    setPremiumFeatureLabel(`${timeLimit}s Pitch`);
                                                    setShowPremiumModal(true);
                                                    return;
                                                }
                                                setSelectedTimeLimit(timeLimit as 30 | 60 | 90);
                                            }}
                                            className={`group relative p-4 rounded-lg border-2 transition-all ${
                                                selectedTimeLimit === timeLimit
                                                    ? getTimeLimitColor(timeLimit)
                                                    : 'border-gray-200 hover:border-gray-300'
                                            } ${locked ? 'opacity-70' : ''}`}
                                        >
                                            <div className="w-full flex flex-col items-center justify-center">
                                                <div className="text-2xl font-bold">{timeLimit}s</div>
                                                <div className="text-sm text-gray-600">
                                                    {timeLimit === 30 ? 'The quick meeting' : 
                                                     timeLimit === 60 ? 'Tell your story' : 
                                                     'The whole "you"'}
                                                </div>
                                            </div>
                                            {locked && (
                                                <div className="absolute -top-3 -right-3 z-10">
                                                    <div className="rounded-full bg-white shadow-md p-1.5 ring-1 ring-slate-200">
                                                        <Lock className="h-6 w-6 text-slate-700" />
                                                    </div>
                                                </div>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Combined Recording and Text Input Section */}
                    <Card>
                        <CardContent className="p-6 sm:p-8">
                            {/* Microphone and Timer - Mobile: stacked, Desktop: side by side */}
                            <div className="flex flex-col sm:flex-row items-center justify-center gap-8 mb-8">
                                {/* Timer display with color coding */}
                                <div className="flex flex-col items-center">
                                    <div className={`text-5xl sm:text-6xl font-bold mb-2 transition-colors duration-300 ${
                                        isRecording 
                                            ? getTimerColor(timeRemaining)
                                            : lastRecordingTime !== null 
                                            ? 'text-blue-600' 
                                            : 'text-gray-400'
                                    }`}>
                                        {isRecording 
                                            ? formatTime(timeRemaining) 
                                            : lastRecordingTime !== null 
                                            ? formatTime(lastRecordingTime)
                                            : formatTime(selectedTimeLimit)
                                        }
                                    </div>
                                    <div className="text-sm text-gray-600">
                                        {isRecording 
                                            ? 'Time Remaining' 
                                            : lastRecordingTime !== null 
                                            ? 'Pitch Length'
                                            : `${selectedTimeLimit} Second Limit`
                                        }
                                    </div>
                                </div>
                                
                                {/* Large microphone button */}
                                <button
                                    onClick={handleMicClick}
                                    disabled={isTranscribing || !isMicrophoneSupported()}
                                    className={`w-24 h-24 sm:w-28 sm:h-28 rounded-full flex items-center justify-center transition-all duration-200 shadow-lg hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-opacity-50 ${
                                        !isMicrophoneSupported() 
                                            ? 'bg-gray-200 border-2 border-gray-300 cursor-not-allowed focus:ring-gray-200' 
                                            : isRecording 
                                            ? 'bg-red-500 hover:bg-red-600 text-white focus:ring-red-200' 
                                            : isTranscribing
                                            ? 'bg-blue-500 text-white cursor-wait focus:ring-blue-200'
                                            : 'bg-primary hover:bg-primary/90 text-white focus:ring-primary/20 hover:scale-105'
                                    }`}
                                    type="button"
                                    aria-label={
                                        !isMicrophoneSupported() ? 'Microphone not supported' :
                                            isRecording ? 'Stop recording' : 'Use microphone for speech to text'
                                    }
                                    title={!isMicrophoneSupported() ? 'Microphone is not supported on this device or browser' : undefined}
                                >
                                    {!isMicrophoneSupported() ? (
                                        <Mic className="w-12 h-12 sm:w-14 sm:h-14 text-gray-400" />
                                    ) : isRecording ? (
                                        <StopCircle className="w-12 h-12 sm:w-14 sm:h-14 text-white" />
                                    ) : isTranscribing ? (
                                        <Hourglass className="w-12 h-12 sm:w-14 sm:h-14 text-white" />
                                    ) : (
                                        <Mic className="w-12 h-12 sm:w-14 sm:h-14 text-white" />
                                    )}
                                </button>
                            </div>

                            {/* Text area below */}
                            <div className="w-full">
                                <Textarea
                                    value={pitchText}
                                    onChange={(e) => setPitchText(e.target.value)}
                                    placeholder="Type your elevator pitch here or use the microphone to record..."
                                    className="w-full min-h-[160px] sm:min-h-[180px] text-base sm:text-lg resize-vertical border-2 focus:border-primary transition-colors rounded-lg p-4"
                                    disabled={isRecording || isTranscribing}
                                />
                            </div>

                            {/* Minimal status indicators */}
                            <div className="mt-6 text-center">
                                {/* Microphone not supported */}
                                {!isMicrophoneSupported() && (
                                    <div className="text-sm text-red-600 bg-red-50 px-4 py-2 rounded-lg inline-block">
                                        Microphone not supported on this device
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Submit Button */}
                    <div className="flex justify-center">
                        <Button
                            onClick={handleSubmit}
                            disabled={
                                isSubmitting ||
                                isGrading ||
                                !pitchText.trim() ||
                                (selectedFocus === 'employment' && !selectedEmploymentId) ||
                                (selectedFocus === 'education' && !selectedEducationId) ||
                                (jobTargeting === 'specific' && !selectedJobId)
                            }
                            className="w-full sm:w-auto px-8 py-3 text-lg"
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                                    The Coach is considering your pitch...
                                </>
                            ) : isGrading ? (
                                <>
                                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                                    {gradingJobName ? `Grading for ${gradingJobName}...` : 'Grading in progress...'}
                                </>
                            ) : (
                                "How'd I do, Coach?"
                            )}
                        </Button>
                    </div>
                </div>

                {/* Premium lock modal */}
                <PremiumLockModal
                    open={showPremiumModal}
                    onOpenChange={(open) => {
                        if (!open) setPremiumFeatureLabel(undefined);
                        setShowPremiumModal(open);
                    }}
                    featureLabel={premiumFeatureLabel ?? 'Pitch'}
                    description={
                        premiumFeatureLabel?.includes('Pitch')
                            ? `Time limits above 30 seconds are for Premium members. Upgrade to unlock ${premiumFeatureLabel.replace(' Pitch','')} recording and other pro features.`
                            : premiumFeatureLabel === 'Target Job'
                            ? 'Targeting a specific job for your pitch is a Premium feature. Upgrade to tailor your practice to a role.'
                            : undefined
                    }
                    ctaLabel="Subscribe now"
                />
            </div>
        );

    return embed ? renderMain() : (
        <DashboardLayout>
            {renderMain()}
        </DashboardLayout>
    );
}
;
export default PitchNew;
